import {useEffect, useState} from "react";
import {App as AntdApp, Alert, Descriptions, Divider, Input, InputNumber, Popconfirm, Progress, Space, Table, Tag} from "antd";
import {PageHeader, Loading, FontAwesomeIcon, Button} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetAdsStatusQuery,
	useGetAdsAuthUrlMutation,
	useGetAdsCustomersQuery,
	useSetAdsCustomerMutation,
	useSetAdsBudgetMutation,
	useDisconnectAdsMutation,
} from "~/reduxs/api/adsApiSlice";

/**
 * Quản trị kết nối Google Ads / Keyword Planner (/admin/google-ads, cap member_manage).
 *
 * MỘT tài khoản Google Ads cấp hệ thống làm nguồn Volume/CPC cho Nghiên cứu từ khoá
 * (KEYWORD_VOLUME_PROVIDER=google_ads) + tab "Gợi ý Google". Luồng: bấm kết nối →
 * OAuth Google (state 'ads.<nonce>', callback dùng chung /gsc/oauth/callback) → chọn
 * customer_id (+ login_customer_id = id MCC khi tài khoản nằm dưới manager; token TEST
 * bắt buộc trỏ MCC test).
 */
function GoogleAdsSettings() {

	const {notification} = AntdApp.useApp();

	const {data: status, isLoading} = useGetAdsStatusQuery();
	const [getAuthUrl, {isLoading: gettingUrl}] = useGetAdsAuthUrlMutation();
	// Chỉ gọi danh sách customer khi đã có tài khoản kết nối còn hiệu lực.
	const hasAccount = !!status?.connected && status.connected.status === 'active' && status.connected.has_ads;
	const {data: customers = [], isFetching: loadingCustomers} = useGetAdsCustomersQuery(undefined, {skip: !hasAccount});
	const [saveCustomer, {isLoading: saving}] = useSetAdsCustomerMutation();
	const [saveBudget, {isLoading: savingBudget}] = useSetAdsBudgetMutation();
	const [disconnect, {isLoading: disconnecting}] = useDisconnectAdsMutation();

	const [customerId, setCustomerId] = useState('');
	const [loginCustomerId, setLoginCustomerId] = useState('');
	const [dailyBudget, setDailyBudget] = useState(15000);

	// Đổ cấu hình đã lưu vào form khi status về.
	useEffect(() => {
		if (status) {
			setCustomerId(status.customer_id || '');
			setLoginCustomerId(status.login_customer_id || '');
			setDailyBudget(status.daily_budget ?? 15000);
		}
	}, [status]);

	const handleConnect = async () => {
		try {
			const res = await getAuthUrl().unwrap();
			if (!res?.url) throw new Error('Không lấy được URL kết nối');
			// Lưu state để callback (/gsc/oauth/callback) đối chiếu chống CSRF nhẹ.
			sessionStorage.setItem('ads_oauth_state', res.state || '');
			window.location.href = res.url;
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thể bắt đầu kết nối Google Ads')});
		}
	};

	const handleSave = async () => {
		try {
			const res = await saveCustomer({customer_id: customerId, login_customer_id: loginCustomerId}).unwrap();
			notification.success({
				message: 'Đã lưu',
				description: res?.ready
					? 'Google Ads đã sẵn sàng phục vụ tra cứu từ khoá.'
					: 'Đã lưu tài khoản sử dụng — kiểm tra lại kết nối nếu chưa sẵn sàng.',
			});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được tài khoản Google Ads')});
		}
	};

	const handleSaveBudget = async () => {
		try {
			await saveBudget({daily_budget: Number(dailyBudget) || 0}).unwrap();
			notification.success({message: 'Đã lưu', description: 'Đã cập nhật trần lượt gọi Google Ads mỗi ngày.'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được ngân sách')});
		}
	};

	const handleDisconnect = async () => {
		try {
			await disconnect().unwrap();
			notification.success({message: 'Đã ngắt kết nối Google Ads'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không ngắt được kết nối')});
		}
	};

	if (isLoading) return <Loading />;

	const revoked = !!status?.connected && status.connected.status === 'revoked';
	const missingScope = !!status?.connected && !status.connected.has_ads;

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-rectangle-ad"
				title="Kết nối Google Ads"
				subtitle="Tài khoản Google Ads cấp hệ thống — nguồn Volume/CPC từ Keyword Planner cho Nghiên cứu từ khoá"
			/>

			{/* Thiếu cấu hình tĩnh .env → hướng dẫn, chưa cho kết nối */}
			{!status?.configured && (
				<Alert
					type="warning" showIcon style={{marginBottom: 16}}
					message="Chưa đủ cấu hình trong .env"
					description={
						<ul style={{margin: 0, paddingLeft: 18}}>
							{!status?.has_developer_token && <li>Thiếu <code>GOOGLE_ADS_DEVELOPER_TOKEN</code> (lấy từ API Center của tài khoản MCC).</li>}
							{!status?.has_oauth_client && <li>Thiếu <code>GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET / GOOGLE_OAUTH_REDIRECT</code>.</li>}
							<li>Sau khi sửa .env, xoá <code>bootstrap/cache/config.php</code> nếu có (config cache làm env() trả rỗng).</li>
						</ul>
					}
				/>
			)}

			<div className="app-card" style={{padding: 24, maxWidth: 720}}>
				{/* Trạng thái tổng */}
				<Space size={8} wrap style={{marginBottom: 16}}>
					{status?.ready
						? <Tag color="green">Sẵn sàng</Tag>
						: <Tag color="orange">Chưa sẵn sàng</Tag>}
					{status?.test_mode && <Tag color="gold">Chế độ test</Tag>}
					<Tag>{status?.provider === 'google_ads' ? 'Nguồn volume: Google Ads' : 'Nguồn volume: DataForSEO'}</Tag>
				</Space>

				{status?.test_mode && (
					<Alert
						type="info" showIcon style={{marginBottom: 16}}
						message="Đang chạy chế độ test (GOOGLE_ADS_TEST_MODE=1)"
						description="Developer token dạng test chỉ gọi được TÀI KHOẢN GOOGLE ADS TEST và Google trả số liệu rỗng — hệ thống tự sinh số liệu demo để kiểm tra giao diện. Khi được duyệt Basic access: trỏ customer sang tài khoản thật và đặt GOOGLE_ADS_TEST_MODE=0."
					/>
				)}

				{revoked && (
					<Alert
						type="error" showIcon style={{marginBottom: 16}}
						message="Kết nối đã bị thu hồi"
						description={`Tài khoản ${status.connected.email} đã bị thu hồi quyền. Vui lòng kết nối lại.`}
					/>
				)}

				{missingScope && !revoked && (
					<Alert
						type="warning" showIcon style={{marginBottom: 16}}
						message="Tài khoản chưa cấp quyền Google Ads"
						description="Tài khoản này kết nối từ GSC/GA trước đây, chưa có scope Google Ads. Bấm kết nối để cấp thêm quyền."
					/>
				)}

				{/* Bước 1: kết nối tài khoản Google */}
				<Descriptions column={1} size="small" style={{marginBottom: 16}}>
					<Descriptions.Item label="Tài khoản Google">
						{status?.connected
							? <Space size={8}>
								<FontAwesomeIcon icon="fa-brands fa-google" />
								<span>{status.connected.email}</span>
								{status.connected.status === 'active' && status.connected.has_ads
									? <Tag color="green">đã kết nối</Tag>
									: <Tag color="red">{revoked ? 'bị thu hồi' : 'thiếu quyền'}</Tag>}
							</Space>
							: <span style={{color: '#9ca3af'}}>Chưa kết nối</span>}
					</Descriptions.Item>
				</Descriptions>

				<Space size={12} wrap>
					<Button primary loading={gettingUrl} disabled={!status?.configured} onClick={handleConnect}
						leftIcon={<FontAwesomeIcon icon="fa-brands fa-google" />}>
						{status?.connected ? 'Kết nối lại / đổi tài khoản' : 'Kết nối Google Ads'}
					</Button>
					{status?.connected && (
						<Popconfirm title="Ngắt kết nối Google Ads?" description="Tra volume sẽ fallback về DataForSEO (nếu có cấu hình)."
							okText="Ngắt kết nối" cancelText="Huỷ" onConfirm={handleDisconnect}>
							<Button loading={disconnecting} leftIcon={<FontAwesomeIcon icon="fa-light fa-plug-circle-xmark" />}>
								Ngắt kết nối
							</Button>
						</Popconfirm>
					)}
				</Space>

				{/* Bước 2: chọn customer id sử dụng */}
				{hasAccount && (
					<>
						<Divider plain style={{margin: '20px 0 16px'}}>Tài khoản Google Ads sử dụng</Divider>

						<div style={{display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))'}}>
							<div>
								<div style={{fontSize: 13, color: '#6b7280', marginBottom: 6}}>
									Customer ID (tài khoản chạy tra cứu)
								</div>
								<Input
									placeholder="Ví dụ: 1234567890"
									value={customerId}
									onChange={(e) => setCustomerId(e.target.value.replace(/[^\d-]/g, ''))}
								/>
								{/* Danh sách customer tài khoản OAuth truy cập được — bấm để điền. */}
								{(loadingCustomers || customers.length > 0) && (
									<div style={{marginTop: 8, fontSize: 12, color: '#9ca3af'}}>
										{loadingCustomers ? 'Đang tải danh sách tài khoản…' : 'Truy cập được:'}
										{!loadingCustomers && customers.map((id) => (
											<Tag key={id} style={{cursor: 'pointer', marginLeft: 6}} onClick={() => setCustomerId(id)}>
												{id}
											</Tag>
										))}
									</div>
								)}
							</div>
							<div>
								<div style={{fontSize: 13, color: '#6b7280', marginBottom: 6}}>
									Login Customer ID (id MCC — điền id MCC <strong>test</strong> khi dùng token test)
								</div>
								<Input
									placeholder="Bỏ trống nếu customer không nằm dưới MCC"
									value={loginCustomerId}
									onChange={(e) => setLoginCustomerId(e.target.value.replace(/[^\d-]/g, ''))}
								/>
							</div>
						</div>

						<div style={{marginTop: 16}}>
							<Button primary small loading={saving} onClick={handleSave}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-floppy-disk" />}>
								Lưu tài khoản sử dụng
							</Button>
						</div>
					</>
				)}

				<Divider plain style={{margin: '20px 0 12px'}} />
				<div style={{fontSize: 13, color: '#6b7280', lineHeight: 1.7}}>
					Nguồn tra volume chọn bằng <code>KEYWORD_VOLUME_PROVIDER</code> trong .env
					(<code>google_ads</code> | <code>dataforseo</code>). Khi Google Ads chưa sẵn sàng,
					hệ thống tự fallback DataForSEO. Độ khó từ khoá (KD) vẫn lấy qua DataForSEO Labs.
				</div>
			</div>

			{/* Ngân sách + giám sát lượt gọi Google Ads mỗi ngày */}
			<div className="app-card" style={{padding: 24, maxWidth: 720, marginTop: 16}}>
				<div style={{fontWeight: 600, fontSize: 15, marginBottom: 6}}>Trần lượt gọi Google Ads mỗi ngày</div>
				<div style={{fontSize: 13, color: '#6b7280', lineHeight: 1.7, marginBottom: 16}}>
					Developer token (Basic access) bị Google giới hạn tổng số lượt tra cứu/ngày (~15.000).
					Khi tổng lượt cả hệ thống chạm trần này, hệ thống <strong>tạm dừng gọi Google</strong> tới
					ngày hôm sau (yêu cầu vẫn nằm trong hàng đợi, không mất lượt của user). Đặt <code>0</code> để
					không giới hạn. Mỗi gói còn có trần riêng theo ngày (chỉnh ở màn Quản lý gói).
				</div>

				{/* Đã dùng hôm nay */}
				{(() => {
					const budget = Number(status?.daily_budget || 0);
					const used = Number(status?.daily_used || 0);
					const pct = budget > 0 ? Math.min(100, Math.round((used / budget) * 100)) : 0;
					return (
						<div style={{marginBottom: 16}}>
							<Space size={8} style={{marginBottom: 6}} wrap>
								<span style={{fontSize: 13, color: '#6b7280'}}>Đã dùng hôm nay:</span>
								<strong>{used.toLocaleString('vi-VN')}</strong>
								<span style={{color: '#9ca3af'}}>/ {budget > 0 ? budget.toLocaleString('vi-VN') : '∞'}</span>
							</Space>
							{budget > 0 && (
								<Progress percent={pct} status={pct >= 100 ? 'exception' : (pct >= 80 ? 'active' : 'normal')} />
							)}
						</div>
					);
				})()}

				<Space size={12} wrap align="end">
					<div>
						<div style={{fontSize: 13, color: '#6b7280', marginBottom: 6}}>Trần lượt / ngày (0 = không giới hạn)</div>
						<InputNumber
							min={0}
							step={1000}
							style={{width: 200}}
							value={dailyBudget}
							onChange={(v) => setDailyBudget(v ?? 0)}
							formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
							parser={(v) => (v || '').replace(/,/g, '')}
						/>
					</div>
					<Button primary small loading={savingBudget} onClick={handleSaveBudget}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-floppy-disk" />}>
						Lưu ngân sách
					</Button>
				</Space>

				{/* Top user tiêu lượt hôm nay */}
				{Array.isArray(status?.top_users) && status.top_users.length > 0 && (
					<>
						<Divider plain style={{margin: '20px 0 12px'}}>Người dùng tra cứu nhiều nhất hôm nay</Divider>
						<Table
							rowKey="user_id"
							size="small"
							pagination={false}
							dataSource={status.top_users}
							columns={[
								{title: 'Người dùng', dataIndex: 'username', key: 'username',
									render: (v, r) => (
										<Space direction="vertical" size={0}>
											<span>{v}</span>
											{r.email ? <span style={{fontSize: 12, color: '#9ca3af'}}>{r.email}</span> : null}
										</Space>
									)},
								{title: 'Lượt tra hôm nay', dataIndex: 'used', key: 'used', align: 'right',
									render: (v) => Number(v).toLocaleString('vi-VN')},
							]}
						/>
					</>
				)}
			</div>
		</div>
	);
}

export default GoogleAdsSettings;
